import React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";

const REPORT_NOTES_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const REPORT_NOTES_FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500";

export function ReportNotesComposer(props: {
  fieldId: string;
  /** When omitted, only description + textarea render (e.g. accordion already shows the section title). */
  label?: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  submitLabel?: string;
  submittingLabel?: string;
  canSubmit: boolean;
  saving: boolean;
  onSubmit: () => void;
  emptyHint?: string;
  showEmptyHint?: boolean;
}): React.ReactElement {
  const description = props.description ?? "Add context for this section.";

  return (
    <div className="w-full" data-shadcn-notes-composer="">
      <Field>
        {props.label ? (
          <FieldLabel htmlFor={props.fieldId} className={REPORT_NOTES_FIELD_LABEL_CLASS}>
            {props.label}
          </FieldLabel>
        ) : null}
        <FieldDescription className={REPORT_NOTES_FIELD_DESC_CLASS}>{description}</FieldDescription>
        <Textarea
          id={props.fieldId}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          placeholder={props.placeholder ?? "Type your note here…"}
          aria-label={props.label ? undefined : "Add a note"}
        />
      </Field>
      <div
        className={
          props.showEmptyHint
            ? "mt-4 flex w-full flex-wrap items-center justify-between gap-3"
            : "mt-4 flex w-full justify-end"
        }
      >
        {props.showEmptyHint ? (
          <p className={`min-w-0 flex-1 ${REPORT_NOTES_FIELD_DESC_CLASS}`}>
            {props.emptyHint ?? "No notes yet."}
          </p>
        ) : null}
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={props.onSubmit}
          disabled={!props.canSubmit || props.saving}
        >
          {props.saving ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              <span>{props.submittingLabel ?? "Adding…"}</span>
            </>
          ) : (
            <span>{props.submitLabel ?? "Add note"}</span>
          )}
        </Button>
      </div>
    </div>
  );
}
